/**
 * Desktop show/hide toggle for the collection filter sidebar.
 *
 * Horizon's vertical facets render as an always-visible column; its only
 * toggle opens the mobile drawer. This injects a Filter button at the left of
 * the toolbar row that swaps `fw-filters-open` on the collection wrapper —
 * the companion CSS hides the sidebar and restores the grid's full span
 * whenever the class is absent, so closed is the default.
 *
 * Filtering and sorting re-render the section, which can replace the toolbar
 * and drop the button; a MutationObserver re-injects it. The open/closed
 * class lives on the collection wrapper, which survives those re-renders, so
 * the state carries across them.
 */
(() => {
  const ICON =
    '<svg viewBox="0 0 21 20" fill="none" aria-hidden="true">' +
    '<path d="M3 5h15M6 10h9M8.5 15h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  const setOpen = (wrapper, open) => {
    wrapper.classList.toggle('fw-filters-open', open);
    wrapper
      .querySelectorAll('.fw-filter-toggle')
      .forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
  };

  /** "2 items" from Horizon's (hidden) products count, as a number. */
  const productCount = (wrapper) => {
    const match = (wrapper.querySelector('.products-count-wrapper')?.textContent || '').match(/\d+/);
    return match ? match[0] : null;
  };

  /* Sticky offset for the toolbar: the header's real rendered height, not the
     theme's --header-height variable, which can run a few pixels over it and
     leave a see-through gap between the two. Re-measured whenever the header
     resizes. */
  const trackHeader = () => {
    const header = document.getElementById('header-component');
    if (!header || header.dataset.fwBarTracked) return;
    header.dataset.fwBarTracked = 'true';
    const apply = () => {
      const bottom = Math.round(header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--fw-bar-top', `${Math.max(0, bottom)}px`);
      // Toolbar pinned under the header: it takes over the header's scroll
      // shadow (html.fw-bar-stuck in fw-collection-styles).
      const bar = document.querySelector('.facets-controls-wrapper');
      const stuck = !!bar && bar.getBoundingClientRect().top <= bottom + 1;
      document.documentElement.classList.toggle('fw-bar-stuck', stuck);
    };
    apply();
    if ('ResizeObserver' in window) new ResizeObserver(apply).observe(header);
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', apply, { passive: true });
  };

  const openInlineSearch = (bar) => {
    const form = bar.querySelector('.fw-search-bar');
    if (!form) return;
    bar.classList.add('fw-search-open');
    form.hidden = false;
    const input = form.querySelector('.fw-search-bar__input');
    // On the search results page, start from the current query.
    input.value = window.location.pathname.startsWith('/search') ? new URLSearchParams(window.location.search).get('q') || '' : '';
    input.focus();
    input.select();
  };

  const closeInlineSearch = (bar) => {
    const form = bar.querySelector('.fw-search-bar');
    if (!form) return;
    form.hidden = true;
    bar.classList.remove('fw-search-open');
    bar.querySelector('.fw-search-toggle')?.focus();
  };

  /* Rotating a tablet across the 900px breakpoint while filters are open: the
     mobile drawer and the desktop panel are different elements, so hand the open
     state from one to the other instead of leaving a closed-looking page with the
     drawer's scroll lock still on. */
  const trackBreakpoint = () => {
    if (document.documentElement.dataset.fwFiltersBpTracked) return;
    document.documentElement.dataset.fwFiltersBpTracked = 'true';
    const desktop = window.matchMedia('(min-width: 900px)');
    let wasDesktop = desktop.matches;
    const onChange = () => {
      if (desktop.matches === wasDesktop) return;
      wasDesktop = desktop.matches;
      const wrapper = document.querySelector('.collection-wrapper');
      const drawer = document.getElementById('filters-drawer');
      if (!wrapper || !drawer) return;
      const drawerOpen = drawer.hasAttribute('open') || !!drawer.querySelector('dialog[open]');
      if (desktop.matches && drawerOpen) {
        drawer.close?.();
        setOpen(wrapper, true);
      } else if (!desktop.matches && wrapper.classList.contains('fw-filters-open')) {
        setOpen(wrapper, false);
        drawer.open?.();
      }
    };
    desktop.addEventListener ? desktop.addEventListener('change', onChange) : desktop.addListener(onChange);
    window.addEventListener('resize', onChange); /* orientation changes fire this even where the media query event is late */
  };

  const inject = () => {
    trackHeader();
    trackBreakpoint();
    syncFacets();
    document.querySelectorAll('.collection-wrapper').forEach((wrapper) => {
      const bar = wrapper.querySelector('.facets-controls-wrapper');
      const sidebar = wrapper.querySelector('.facets-block-wrapper--vertical:not(#filters-drawer)');
      if (!bar || !sidebar) return;

      // Filter & sort button: rendered by blocks/filters.liquid; created here
      // only as a fallback. Wired once.
      let button = bar.querySelector('.fw-filter-toggle');
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'fw-filter-toggle';
        button.innerHTML = `${ICON}<span>Filter &amp; sort</span>`;
        bar.prepend(button);
      }
      button.setAttribute('aria-expanded', String(wrapper.classList.contains('fw-filters-open')));
      if (!button.dataset.fwWired) {
        button.dataset.fwWired = 'true';
        button.addEventListener('click', () => {
          // Below the desktop breakpoint the drawer element handles filters;
          // the overlay panel is desktop-only CSS.
          if (window.innerWidth < 900) {
            document.getElementById('filters-drawer')?.open();
            return;
          }
          setOpen(wrapper, !wrapper.classList.contains('fw-filters-open'));
        });
      }

      // Search button at the right-hand end, same arrangement.
      let search = bar.querySelector('.fw-search-toggle');
      if (!search) {
        search = document.createElement('button');
        search.type = 'button';
        search.className = 'fw-search-toggle';
        search.setAttribute('aria-haspopup', 'dialog');
        search.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.5"/>' +
          '<path d="m16 16 4.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '</svg><span>Search</span>';
        bar.append(search);
      }
      if (!search.dataset.fwWired) {
        search.dataset.fwWired = 'true';
        search.addEventListener('click', () => openInlineSearch(bar));
      }

      // Inline search: the toolbar turns into a search field with a confirm
      // (tick) and a cancel (X) at its right end. Built once per bar.
      if (!bar.querySelector('.fw-search-bar')) {
        const form = document.createElement('form');
        form.className = 'fw-search-bar';
        form.action = '/search';
        form.method = 'get';
        form.setAttribute('role', 'search');
        form.hidden = true;
        form.innerHTML =
          '<input type="hidden" name="type" value="product">' +
          '<input class="fw-search-bar__input" type="search" name="q" placeholder="Search necklaces, bracelets, earrings" autocomplete="off" aria-label="Search">' +
          '<button class="fw-search-bar__submit" type="submit" aria-label="Search">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>' +
          '</button>' +
          '<button class="fw-search-bar__close" type="button" aria-label="Close search">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
          '</button>';
        form.querySelector('.fw-search-bar__close').addEventListener('click', () => closeInlineSearch(bar));
        form.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') closeInlineSearch(bar);
        });
        form.addEventListener('submit', (event) => {
          if (!form.querySelector('.fw-search-bar__input').value.trim()) event.preventDefault();
        });
        bar.append(form);
      }

      // Sort lives inside the filter panel ("Filter & sort"), not the toolbar:
      // last row, after the facet groups. It is placed OUTSIDE the facets
      // <form> (before the actions row): inside the form its sort_by select
      // counts as a filter change and the section re-renders in a loop,
      // snapping the panel and the mobile drawer shut. The select-based
      // variant renders there and the popover one is hidden by CSS.
      // Re-renders can put it back in the bar, so this runs each pass.
      const sort = bar.querySelector('sorting-filter-component');
      if (sort) {
        sort.classList.add('fw-sidebar-sort');
        const actionsRow = sidebar.querySelector('.fw-filter-actions');
        if (actionsRow) sidebar.insertBefore(sort, actionsRow);
        else sidebar.appendChild(sort);
      }

      // Close (X) in the panel's top-right corner, on the Filter heading line.
      if (!sidebar.querySelector('.fw-filter-close')) {
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'fw-filter-close';
        close.setAttribute('aria-label', 'Close filters');
        close.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '</svg>';
        close.addEventListener('click', () => setOpen(wrapper, false));
        sidebar.prepend(close);
      }

      // Clicking anywhere on a Sort row (label, blank space) opens its select.
      document.querySelectorAll('.fw-sidebar-sort .sorting-filter__container, #filters-drawer .sorting-filter__container').forEach((row) => {
        if (row.dataset.fwSortWired) return;
        row.dataset.fwSortWired = 'true';
        row.addEventListener('click', (event) => {
          const select = row.querySelector('select');
          if (!select || event.target === select) return;
          event.preventDefault();
          try {
            if (typeof select.showPicker === 'function') select.showPicker();
            else select.focus();
          } catch (e) {
            select.focus();
          }
        });
      });

      // Bottom actions row. Filters apply live, so VIEW N PRODUCTS just
      // closes the drawer onto the already-filtered grid.
      let actions = sidebar.querySelector('.fw-filter-actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'fw-filter-actions';

        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'fw-filter-actions__clear';
        clear.textContent = 'Clear all';
        clear.addEventListener('click', () => {
          const themeClear = sidebar.querySelector('.facets__clear-all-link, .facets__clear-all');
          if (themeClear) themeClear.click();
          else window.location.href = window.location.pathname;
        });

        const view = document.createElement('button');
        view.type = 'button';
        view.className = 'fw-filter-actions__view';
        view.addEventListener('click', () => setOpen(wrapper, false));

        actions.append(clear, view);
        sidebar.appendChild(actions);
      }

      const view = actions.querySelector('.fw-filter-actions__view');
      const count = productCount(wrapper);
      const label = count === '1' ? 'View 1 product' : count ? `View ${count} products` : 'View products';
      if (view.textContent !== label) view.textContent = label;

      // Mobile drawer's apply button, matched to the desktop wording.
      const seeResults = document.querySelector('#filters-drawer .facets__see-results');
      if (seeResults) {
        const n = (seeResults.textContent.match(/\d+/) || [null])[0];
        const wanted = n === '1' ? 'View 1 product' : n ? `View ${n} products` : 'View products';
        if (seeResults.textContent.trim() !== wanted) seeResults.textContent = wanted;
      }

      // Scrim clicks land on the wrapper itself — its ::after is the scrim.
      if (!wrapper.dataset.fwScrimWired) {
        wrapper.dataset.fwScrimWired = 'true';
        wrapper.addEventListener('click', (event) => {
          if (event.target === wrapper) setOpen(wrapper, false);
        });
      }
    });
  };

  /**
   * One filter section open at a time, in both the desktop panel and the
   * mobile drawer. `toggle` does not bubble, so it is caught in the capture
   * phase; closing siblings re-fires toggle for them, which the `open` guard
   * ignores.
   */
  const exclusiveAccordions = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLDetailsElement)) return;
    if (!panel.classList.contains('facets__panel')) return;
    if (!panel.open) return;
    if (!panel.closest('.facets--vertical, .facets--drawer')) return;

    const scope = panel.closest('.facets__filters-wrapper');
    if (!scope) return;

    scope.querySelectorAll('details.facets__panel[open]').forEach((other) => {
      if (other !== panel) other.removeAttribute('open');
    });
  };

  /**
   * Desktop Pearl Color / Pearl Size / Pearl Quality dropdowns in the toolbar
   * (blocks/filters.liquid). A tick is mirrored onto the matching checkbox in
   * the sidebar's facets form, so Horizon filters and re-renders as it would
   * from the panel; without a match it follows the value's filtered URL. The
   * re-render redraws the dropdowns closed, so the open one is remembered and
   * reopened on the next pass.
   */
  let openFacet = null;

  const setFacetOpen = (facet, open) => {
    facet.dataset.open = String(open);
    facet.querySelector('.fw-facet__trigger')?.setAttribute('aria-expanded', String(open));
    const panel = facet.querySelector('.fw-facet__panel');
    if (panel) panel.hidden = !open;
  };

  const syncFacets = () => {
    document.querySelectorAll('.fw-facet').forEach((facet) => {
      const open = facet.dataset.fwFacet === openFacet;
      if ((facet.dataset.open === 'true') !== open) setFacetOpen(facet, open);
    });
  };

  /** A sort order from the toolbar's Sort list, applied through the sidebar's radios. */
  const applySort = (option) => {
    const value = option.dataset.sort;
    openFacet = null;
    syncFacets();
    const radio = option
      .closest('.collection-wrapper')
      ?.querySelector(`.fw-sidebar-sort input[name="sort_by"][value="${CSS.escape(value)}"]`);
    if (radio) {
      if (!radio.checked) radio.click();
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    window.location.href = url.toString();
  };

  const onFacetClick = (event) => {
    // Clear all in the toolbar: through the sidebar's facets form, like the
    // panel's own Clear all, so the grid re-renders without a page load.
    const clearAll = event.target.closest?.('.fw-bar-clear');
    if (clearAll) {
      const form = clearAll
        .closest('.collection-wrapper')
        ?.querySelector('.facets-block-wrapper--vertical:not(#filters-drawer) facets-form-component');
      if (form && typeof form.updateFiltersByURL === 'function') {
        event.preventDefault();
        openFacet = null;
        syncFacets();
        form.updateFiltersByURL(clearAll.getAttribute('href'));
      }
      return;
    }
    const sortOption = event.target.closest?.('.fw-facet__sort-opt');
    if (sortOption) {
      applySort(sortOption);
      return;
    }
    const trigger = event.target.closest?.('.fw-facet__trigger');
    if (trigger) {
      const facet = trigger.closest('.fw-facet');
      openFacet = facet.dataset.open === 'true' ? null : facet.dataset.fwFacet;
      syncFacets();
      return;
    }
    // Only a real click outside closes it: the mirrored tick's own .click()
    // on the sidebar input lands outside too.
    if (event.isTrusted && !event.target.closest?.('.fw-facet__panel') && openFacet) {
      openFacet = null;
      syncFacets();
    }
  };

  const onFacetChange = (event) => {
    const check = event.target;
    if (!check.classList?.contains('fw-facet__check')) return;
    const form = check.closest('.collection-wrapper')?.querySelector('.facets-block-wrapper--vertical:not(#filters-drawer) form');
    const mirror = form?.querySelector(
      `input[type="checkbox"][name="${CSS.escape(check.dataset.name)}"][value="${CSS.escape(check.value)}"]`
    );
    if (mirror) {
      if (mirror.checked !== check.checked) mirror.click();
    } else if (check.dataset.url) {
      window.location.href = check.dataset.url;
    }
  };

  const start = () => {
    inject();
    document.addEventListener('click', onFacetClick);
    document.addEventListener('change', onFacetChange);
    document.addEventListener('toggle', exclusiveAccordions, true);
    new MutationObserver(inject).observe(document.body, { childList: true, subtree: true });
    // Horizon's re-render morphs the dropdowns back to closed by attribute
    // alone, which the childList observer above doesn't see. syncFacets only
    // writes on a mismatch, so its own writes settle at once.
    new MutationObserver(syncFacets).observe(document.body, {
      attributes: true,
      attributeFilter: ['hidden', 'data-open'],
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', inject);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (openFacet) {
      openFacet = null;
      syncFacets();
    }
    document
      .querySelectorAll('.collection-wrapper.fw-filters-open')
      .forEach((wrapper) => setOpen(wrapper, false));
  });
})();
